import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Stethoscope, FileText, Video, ArrowRight } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AppointmentModal } from '../components/AppointmentModal';
import { NavBar } from '../components/NavBar';
import { Footer } from '../components/Footer';
import { PageHead } from '../components/PageHead';
import { StructuredData, breadcrumb } from '../components/StructuredData';
import {
  DOCTOLIB_LUNETTERIE_RUSSEY,
  DOCTOLIB_OPHTALMO_BESANCON,
} from '../doctolibLinks';
import { track } from '../utils/umami';
import '../App.css';

export function LunettesPage() {
  const navigate = useNavigate();
  const [step, setStep]             = useState('menu');
  const [age, setAge]               = useState('');
  const [ageError, setAgeError]     = useState('');
  const [ageResult, setAgeResult]   = useState(null);
  const [modalOpen, setModalOpen]   = useState(false);
  const [prefill, setPrefill]       = useState(null);

  const reset = useCallback(() => {
    setStep('menu');
    setAge('');
    setAgeError('');
    setAgeResult(null);
  }, []);

  const submitAge = useCallback(() => {
    setAgeError('');
    const n = parseInt(age, 10);
    if (age.trim() === '' || Number.isNaN(n) || n < 0) {
      setAgeError("Indiquez votre âge en années entières, s'il vous plaît.");
      return;
    }
    const orientation = n >= 16 && n <= 75 ? 'russey' : 'besancon';
    track('parcours-lunettes-resultat', { orientation });
    setAgeResult(orientation);
    setStep('result');
  }, [age]);

  return (
    <div className="App">
      <PageHead
        title="Lunettes de vue – Lunetterie du Russey | Le Russey (25210)"
        description="Besoin de lunettes de vue au Russey ? Renouvellement, première ordonnance ou examen de vue : trouvez le bon parcours en quelques clics. Prise de RDV en ligne."
      />
      <StructuredData data={breadcrumb([
        { name: 'Accueil', path: '/' },
        { name: 'Lunettes de vue', path: '/lunettes' },
      ])} />
      <NavBar />

      <section id="lunettes" className="lunettes-section page-section-first wave-bottom-to-dark" aria-labelledby="lunettes-heading">
        <div className="section-container">
          <div className="section-header">
            <Badge className="section-badge">Lunettes de vue</Badge>
            <h1 className="section-title" id="lunettes-heading">
              Par où commencer pour vos prochaines lunettes ?
            </h1>
            <p className="section-description lunettes-section-lead">
              Premier essai, renouvellement ou simple envie de changement : indiquez d&apos;où vous partez.
            </p>
          </div>

          {step === 'menu' && (
            <div className="lunettes-choice-grid" role="group" aria-label="Votre situation actuelle">
              <button type="button" className="lunettes-choice-card"
                onClick={() => { track('parcours-lunettes', { etape: 'jamais-ophtalmo' }); setStep('age'); setAgeError(''); setAge(''); }}>
                <div className="lunettes-choice-icon" aria-hidden><Stethoscope className="lunettes-choice-icon-svg" /></div>
                <h2 className="lunettes-choice-title">Je n&apos;ai jamais consulté d&apos;ophtalmologiste</h2>
                <p className="lunettes-choice-text">
                  Aucune ordonnance récente, ou c&apos;est toute la première démarche ? Une question toute simple nous
                  aide à orienter le bon parcours de soins, selon la réglementation.
                </p>
                <span className="lunettes-choice-cta">C&apos;est mon cas <ArrowRight className="inline h-4 w-4" aria-hidden /></span>
              </button>

              <button type="button" className="lunettes-choice-card lunettes-choice-card--accent"
                onClick={() => { track('parcours-lunettes', { etape: 'j-ai-ordonnance' }); navigate('/ordonnance'); }}>
                <div className="lunettes-choice-icon" aria-hidden><FileText className="lunettes-choice-icon-svg" /></div>
                <h2 className="lunettes-choice-title">J&apos;ai une ordonnance de lunettes</h2>
                <p className="lunettes-choice-text">
                  Votre feuille d&apos;ophtalmo est prête, ou proche d&apos;expirer : vérifions ensemble si elle
                  est encore valable, puis on planifie votre passage au magasin.
                </p>
                <span className="lunettes-choice-cta">Vérifier mon ordonnance <ArrowRight className="inline h-4 w-4" aria-hidden /></span>
              </button>
            </div>
          )}

          {step === 'age' && (
            <Card className="lunettes-form-card max-w-lg mx-auto shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl">Quel âge avez-vous ?</CardTitle>
                <CardDescription className="text-base text-[#4a4a4a] leading-relaxed">
                  Selon l&apos;âge, le parcours n&apos;est pas le même : le Russey accueille la
                  télésurveillance en magasin sur une tranche d&apos;âge précise ; en dehors, la
                  métropole de référence (Besançon) reste le relais le plus sûr.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={(e) => { e.preventDefault(); submitAge(); }} className="space-y-4">
                  <div className="input-group max-w-xs">
                    <Label htmlFor="lunettes-age" className="text-base">Âge (années entières)</Label>
                    <Input id="lunettes-age" type="number" inputMode="numeric" min={0} max={120}
                      value={age} onChange={(e) => { setAge(e.target.value); setAgeError(''); }} placeholder="Ex. 32" />
                    {ageError && <p className="text-sm text-red-600" role="alert">{ageError}</p>}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <Button type="submit" className="w-full sm:w-auto bg-[var(--color-forest)] hover:opacity-95 text-white">
                      Voir mon orientation
                    </Button>
                    <Button type="button" variant="ghost" onClick={reset}>Retour</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {step === 'result' && ageResult === 'russey' && (
            <Card className="lunettes-result-card max-w-2xl mx-auto">
              <CardContent className="pt-8 pb-8 px-6">
                <div className="flex items-start gap-3 mb-4">
                  <Video className="h-10 w-10 shrink-0 text-[var(--color-rust)]" aria-hidden />
                  <div>
                    <h2 className="text-2xl font-bold text-[var(--color-anthracite)] mb-2" style={{ fontFamily: "'Roboto Slab', serif" }}>
                      Vous pouvez poursuivre au Russey
                    </h2>
                    <p className="text-base leading-relaxed text-[#444] mb-4">
                      De 16 à 75 ans, vous pouvez bénéficier d&apos;un parcours complet au Russey, avec la borne
                      MEDADOM (téléconsultation ophtalmologique) pour obtenir une ordonnance adaptée, puis notre
                      suivi d&apos;opticien. Réservez votre rendez-vous en ligne.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                      <Button asChild className="bg-[var(--color-rust)] hover:bg-[var(--color-rust)]/90 text-white w-full sm:w-auto">
                        <a href={DOCTOLIB_LUNETTERIE_RUSSEY} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2" onClick={() => track('clic-doctolib', { type: 'teleconsultation-russey' })}>
                          <ExternalLink className="h-4 w-4" />
                          Téléconsultation (Doctolib) — Pôle santé
                        </a>
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground mt-4">
                      Avec l&apos;ordonnance, faites le pas suivant : essayage, conseils, mesures — on prépare ensemble vos prochaines lunettes en magasin.
                    </p>
                    <Button type="button" variant="link" className="mt-4 p-0 h-auto text-[var(--color-anthracite)]" onClick={reset}>
                      Changer d&apos;avis
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {step === 'result' && ageResult === 'besancon' && (
            <Card className="lunettes-result-card max-w-2xl mx-auto">
              <CardContent className="pt-8 pb-8 px-6">
                <h2 className="text-2xl font-bold text-[var(--color-anthracite)] mb-3" style={{ fontFamily: "'Roboto Slab', serif" }}>
                  Passez d&apos;abord par l&apos;ophtalmologiste
                </h2>
                <p className="text-base leading-relaxed text-[#444] mb-4">
                  Pour les moins de 16 ans et les personnes de plus de 75 ans, le suivi s&apos;organise
                  d&apos;abord en cabinet, avec l&apos;ophtalmologiste. Besançon, métropole la plus proche,
                  concentre une offre de rendez-vous. Une fois l&apos;ordonnance en main, la Lunetterie
                  s&apos;occupe du choix de monture et de verre avec vous, au pôle santé.
                </p>
                <Button asChild className="w-full sm:w-auto bg-[var(--color-anthracite)] hover:opacity-95 text-white">
                  <a href={DOCTOLIB_OPHTALMO_BESANCON} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2" onClick={() => track('clic-doctolib', { type: 'ophtalmo-besancon', source: 'lunettes' })}>
                    <ExternalLink className="h-4 w-4" />
                    Rechercher un ophtalmologue (Besançon)
                  </a>
                </Button>
                <Button type="button" variant="link" className="mt-4 p-0 h-auto block text-[var(--color-anthracite)]"
                  onClick={() => { setStep('age'); setAgeResult(null); setAgeError(''); }}>
                  Modifier mon âge
                </Button>
                <Button type="button" variant="link" className="mt-1 p-0 h-auto block text-[var(--color-anthracite)]" onClick={reset}>
                  Revenir à l&apos;écran d&apos;accueil
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      <Footer />

      <AppointmentModal
        open={modalOpen}
        onOpenChange={(o) => { setModalOpen(o); if (!o) setPrefill(null); }}
        prefillData={prefill}
      />
    </div>
  );
}
